import { QueryFunctionContext, queryOptions } from "@tanstack/react-query";
import { api, type QueryParams } from "services/restUtil";
import { BaseApiQueryOptions, QueryError } from "./utils";

// Full query keys are `[key, params, payload]`.
// The key segment is a stable cache prefix. For path-like keys, keep the
// declared route pattern rather than the interpolated URL, so all variants of
// `/skills/category/:categoryId` share the same `mainKey`.
export type TalaeraQueryKey<
  TQueryParams extends QueryKeyParams = void,
  TPayload = void,
> = readonly [key: string, queryParams: TQueryParams, payload: TPayload];

export type QueryKeyParams = QueryParams | void | undefined;

/**
 * Shared config for Talaera query factories.
 *
 * `key` is the stable cache key prefix used in `[key, params, payload]`.
 * For plain GET queries it is also passed directly to `api.get` as the path.
 */
type ApiQueryConfig<TResponse, TParams extends QueryKeyParams, TPayload, TData> = {
  key: string;
  queryFn?: QueryFn<TResponse, TParams, TPayload>;
} & BaseApiQueryOptions<TResponse, TParams, TPayload, TData>;

type QueryFn<TResponse, TParams extends QueryKeyParams, TPayload = void> = (args: {
  key: string;
  params: TParams;
  payload: TPayload;
  context: QueryFunctionContext<TalaeraQueryKey<TParams, TPayload>>;
}) => Promise<TResponse>;

/**
 * Creates a query-options factory with Talaera's standard cache key shape.
 *
 * `queryFn` can be omitted for plain GET requests; the default is
 * `api.get(key, params)`. Custom `queryFn`s receive `key`, `params`, and `payload`.
 */
export function defineApiQuery<
  TResponse,
  TParams extends QueryKeyParams = void,
  TPayload = void,
  TData = TResponse,
>(config: ApiQueryConfig<TResponse, TParams, TPayload, TData>) {
  const defaultQueryFn: QueryFn<TResponse, TParams, TPayload> = async args => {
    // Cache-key params are intentionally looser than wire params (custom queryFns may
    // carry rich shapes); queries relying on this default GET must use scalar params.
    return api.get<TResponse>(args.key, (args.params ?? null) as QueryParams | null);
  };

  const { key, queryFn = defaultQueryFn, ...restConfig } = config;

  const wrappedQueryFn = (
    context: QueryFunctionContext<TalaeraQueryKey<TParams, TPayload>>
  ) => {
    const [, params, payload] = context.queryKey;
    return queryFn({ key, params, payload, context }).catch(error => {
      const queryError = error instanceof QueryError ? error : new QueryError(error);
      throw queryError;
    });
  };

  function createQueryOptions(params: TParams, payload: TPayload) {
    return queryOptions({
      ...restConfig,
      queryKey: [key, params, payload] as const,
      queryFn: wrappedQueryFn,
    });
  }

  createQueryOptions.queryKeyPrefix = [key];

  return createQueryOptions as typeof createQueryOptions & {
    queryKeyPrefix: [TalaeraQueryKey<TParams, TPayload>[0]];
  };
}

export { defineInfiniteApiQuery } from "./infiniteQueries";
export type { PaginatedResponse } from "./infiniteQueries";
export { QueryError } from "./utils";
